import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Gift, Loader2, TrendingUp, Check, XCircle } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { PLAN_LABELS } from "../lib/plans";

export default function AgencyBilling() {
  const navigate = useNavigate();
  const user = auth.current();
  const [usage, setUsage] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState("");
  const [redeemErr, setRedeemErr] = useState("");
  const [upgrading, setUpgrading] = useState("");
  const [upgradeErr, setUpgradeErr] = useState("");
  const [previewing, setPreviewing] = useState("");
  const [pendingPlan, setPendingPlan] = useState(null); // { plan, amount }
  const [canceling, setCanceling] = useState(false);
  const [cancelErr, setCancelErr] = useState("");
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [canceledUntil, setCanceledUntil] = useState(null);

  const slug = user?.agency_slug || "default";

  const load = () => {
    api.get(`/agencies/${slug}/usage`).then(({ data }) => setUsage(data)).catch(() => {});
  };

  useEffect(() => {
    if (!user || !["master", "agency_admin"].includes(user.role)) { navigate("/admin/login"); return; }
    load();
  }, []);

  if (!user) return null;

  const redeemCode = async (e) => {
    e.preventDefault();
    setRedeemErr("");
    setRedeemMsg("");
    setRedeeming(true);
    try {
      const { data } = await api.post(`/agencies/${slug}/redeem-code`, { code });
      setRedeemMsg(
        data.type === "extra_trips"
          ? `¡Listo! Se agregaron ${data.value} viajes extra a tu cuenta.`
          : "Código aplicado. Se reflejará en tu próxima factura."
      );
      setCode("");
      load();
    } catch (e) {
      setRedeemErr(e?.response?.data?.detail || "No se pudo canjear el código");
    } finally {
      setRedeeming(false);
    }
  };

  const upgrade = async (plan) => {
    setUpgradeErr("");
    setPreviewing(plan);
    try {
      const { data: preview } = await api.post(`/agencies/${slug}/preview-plan-change`, { plan });
      setPreviewing("");
      if (preview.requires_confirmation) {
        setPendingPlan({ plan, amount: preview.amount_due_mxn });
        return;
      }
      await doUpgrade(plan);
    } catch (e) {
      setPreviewing("");
      setUpgradeErr(e?.response?.data?.detail || "No se pudo calcular el cambio de plan");
    }
  };

  const doUpgrade = async (plan) => {
    setUpgrading(plan);
    setUpgradeErr("");
    try {
      const { data } = await api.post(`/agencies/${slug}/create-checkout-session`, {
        plan,
        success_url: window.location.href,
        cancel_url: window.location.href,
      });
      if (data.updated_existing_subscription) {
        // Ya tenía una suscripción viva — se actualizó el precio ahí mismo
        // en vez de abrir un checkout nuevo (evita cobrar dos veces).
        toast.success(`Plan actualizado a ${PLAN_LABELS[plan]}.`);
        load();
        setUpgrading("");
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      setUpgradeErr(e?.response?.data?.detail || "No se pudo iniciar el pago");
      setUpgrading("");
    }
  };

  const cancelSubscription = async () => {
    setCanceling(true);
    setCancelErr("");
    try {
      const { data } = await api.post(`/agencies/${slug}/cancel-subscription`);
      setCancelConfirm(false);
      setCanceledUntil(data.current_period_end);
      toast.success("Suscripción cancelada — sigue activa hasta el fin del período ya pagado.");
      load();
    } catch (e) {
      setCancelErr(e?.response?.data?.detail || "No se pudo cancelar la suscripción");
    } finally {
      setCanceling(false);
    }
  };

  const isPaid = usage && ["active", "trialing", "past_due"].includes(usage.subscription_status);
  const pct = usage?.trips_included
    ? Math.min(100, Math.round((usage.trips_this_month / (usage.trips_included + usage.bonus_trips)) * 100))
    : 0;

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-12 space-y-8">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Facturación</p>
          <h1 className="font-display text-5xl font-light tracking-tight">
            Mi <span className="text-[var(--brand-cyan,#FF85AC)]">Plan</span>
          </h1>
        </div>

        {usage && (
          <article className="glass rounded-3xl p-7 border border-white/10">
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
                <h2 className="font-display text-xl">Plan {PLAN_LABELS[usage.plan]}</h2>
                <span
                  data-testid="subscription-status-badge"
                  className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
                    isPaid
                      ? "text-[var(--brand-cyan,#FF85AC)] border-[var(--brand-cyan,#FF85AC)]/30"
                      : "text-neutral-500 border-white/10"
                  }`}
                >
                  {isPaid ? "Pagado" : "Asignado, sin pago activo"}
                </span>
              </div>
              {usage.price_mxn != null && (
                <p className="text-2xl font-display">${usage.price_mxn.toLocaleString()} <span className="text-sm text-neutral-500">MXN/mes</span></p>
              )}
            </div>

            <div className="mb-2 flex items-center justify-between text-sm text-neutral-400">
              <span>Viajes este mes</span>
              <span>
                {usage.trips_this_month} / {usage.trips_included == null ? "ilimitados" : usage.trips_included + usage.bonus_trips}
              </span>
            </div>
            {usage.trips_included != null && (
              <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden mb-2">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, backgroundColor: "var(--brand-cyan, #FF85AC)" }}
                />
              </div>
            )}
            {usage.bonus_trips > 0 && (
              <p className="text-xs text-[var(--brand-cyan,#FF85AC)] mb-4">+{usage.bonus_trips} viajes extra por código promocional</p>
            )}
            {usage.trips_included != null && usage.trips_remaining === 0 && (
              <p className="text-sm text-[#FF3B30] mb-4">
                Ya usaste todos los viajes incluidos este mes — ${usage.extra_trip_block_price_mxn} MXN por cada {usage.extra_trip_block_size} viajes extra, o sube de plan abajo.
              </p>
            )}

            <div className="pt-5 border-t border-white/10">
              <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">
                {isPaid ? "Cambiar de plan" : "Activar / cambiar de plan"}
              </p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(PLAN_LABELS).filter(([v]) => isPaid ? v !== usage.plan : true).map(([v, label]) => (
                  <button
                    key={v}
                    onClick={() => upgrade(v)}
                    disabled={!!upgrading || !!previewing}
                    data-testid={`upgrade-to-${v}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:border-[var(--brand-cyan,#FF85AC)]/50 text-sm disabled:opacity-50"
                  >
                    {upgrading === v || previewing === v ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                    {v === usage.plan && !isPaid ? `Activar ${label}` : label}
                  </button>
                ))}
              </div>
              {upgradeErr && <p className="text-sm text-[#FF3B30] mt-3" data-testid="upgrade-error">{upgradeErr}</p>}
              <p className="text-xs text-neutral-600 mt-3">
                Si el pago en línea todavía no está activo, este botón te avisará — mientras tanto, contáctanos directo para cambiar de plan.
              </p>
            </div>

            {isPaid && (
              <div className="pt-5 mt-5 border-t border-white/10">
                {canceledUntil ? (
                  <p className="text-sm text-neutral-400" data-testid="cancel-scheduled-notice">
                    Tu suscripción no se va a renovar — sigue activa hasta el{" "}
                    {new Date(canceledUntil * 1000).toLocaleDateString()}.
                  </p>
                ) : cancelConfirm ? (
                  <div className="p-4 rounded-2xl bg-[#FF3B30]/10 border border-[#FF3B30]/30" data-testid="cancel-confirm-box">
                    <p className="text-sm text-neutral-200 mb-4">
                      ¿Cancelar tu suscripción? Sigues con acceso hasta el fin del período que ya
                      pagaste — después no se renueva.
                    </p>
                    {cancelErr && <p className="text-sm text-[#FF3B30] mb-3">{cancelErr}</p>}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setCancelConfirm(false)}
                        className="flex-1 py-2.5 rounded-full border border-white/15 text-sm hover:border-white/30"
                      >
                        Volver
                      </button>
                      <button
                        onClick={cancelSubscription}
                        disabled={canceling}
                        data-testid="confirm-cancel-subscription"
                        className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full font-semibold bg-[#FF3B30] text-white hover:bg-[#FF5147] disabled:opacity-50"
                      >
                        {canceling && <Loader2 className="w-4 h-4 animate-spin" />}
                        Sí, cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setCancelConfirm(true)}
                    data-testid="cancel-subscription-btn"
                    className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-[#FF3B30]"
                  >
                    <XCircle className="w-4 h-4" /> Cancelar suscripción
                  </button>
                )}
              </div>
            )}
          </article>
        )}

        <article className="glass rounded-3xl p-7 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <Gift className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Canjear código</h2>
          </div>
          <form onSubmit={redeemCode} className="flex flex-wrap gap-3">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="CÓDIGO"
              data-testid="redeem-code-input"
              className="flex-1 min-w-[160px] bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white font-mono"
            />
            <button
              type="submit"
              disabled={redeeming || !code.trim()}
              data-testid="redeem-code-btn"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold bg-[var(--brand-cyan,#FF85AC)] text-black hover:bg-[#FF9DC0] disabled:opacity-50"
            >
              {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Canjear
            </button>
          </form>
          {redeemMsg && <p className="text-sm text-[var(--brand-cyan,#FF85AC)] mt-3" data-testid="redeem-success">{redeemMsg}</p>}
          {redeemErr && <p className="text-sm text-[#FF3B30] mt-3" data-testid="redeem-error">{redeemErr}</p>}
        </article>
      </section>

      {pendingPlan && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="plan-change-confirm-modal"
        >
          <div className="max-w-md w-full glass rounded-3xl p-8 border border-white/10 cyan-glow">
            <h3 className="font-display text-xl mb-3">Confirmar cambio de plan</h3>
            <p className="text-sm text-neutral-300 leading-relaxed mb-6">
              Cambiar a <b className="text-white">{PLAN_LABELS[pendingPlan.plan]}</b>
              {pendingPlan.amount > 0 && (
                <>
                  {" "}se cobrará de inmediato un ajuste prorrateado de{" "}
                  <b className="text-[var(--brand-cyan,#FF85AC)]">
                    ${pendingPlan.amount.toLocaleString()} MXN
                  </b>{" "}
                  a tu método de pago.
                </>
              )}
              {pendingPlan.amount < 0 && (
                <>
                  {" "}te dejará un crédito de{" "}
                  <b className="text-[var(--brand-cyan,#FF85AC)]">
                    ${Math.abs(pendingPlan.amount).toLocaleString()} MXN
                  </b>{" "}
                  a favor en tu próxima factura.
                </>
              )}
              {pendingPlan.amount === 0 && " no tiene ningún cargo adicional en este momento."}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingPlan(null)}
                data-testid="cancel-plan-change"
                className="flex-1 py-2.5 rounded-full border border-white/15 text-sm hover:border-white/30"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const plan = pendingPlan.plan;
                  setPendingPlan(null);
                  doUpgrade(plan);
                }}
                data-testid="confirm-plan-change"
                className="flex-1 py-2.5 rounded-full font-semibold bg-[var(--brand-cyan,#FF85AC)] text-black hover:bg-[#FF9DC0]"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
